import { IconNote, IconTasks } from '../../../common/icons';
import Picker from '../../../common/components/picker/picker';
import { repositoryOptions, resolveRepository } from '../../repositories/contexts/repositories';
import { resolveTier, tierOptions } from '../../tiers/contexts/tiers';
import {
    ESTIMATE_OPTIONS, STATUS_OPTIONS, TASK_KIND_OPTIONS, TITLE_MAX,
} from '../utils/form';

import '../../clients/styles/form.css';

// `placementLocked` is true while editing: PATCH takes neither tier_id nor
// repository_id. A task belongs to exactly ONE repository, and moving it would
// leave "which repo does the agent clone" without an answer. The fields still
// show — the screen has to say where this task lives — but they cannot change.
//
// TIER AND REPOSITORY ARE PICKERS AND NOT `<select limit:100>`, which is the
// fix and not a flourish. The old selects asked for the first hundred rows and
// hoped: past that, the right tier could not be chosen at all, and a task
// already pointing at one of them rendered as `#57` — which reads as missing
// data rather than as a catalog that ran out. Neither endpoint takes `q`, so
// the picker narrows by project instead of searching by text, and resolves the
// current value by id when it is not in the page.

export default function TaskFields({
    values, errors = {}, onChange, disabled, placementLocked, weightLocked, visible,
    projectId, tierLabel, repositoryLabel,
}) {
    const set = (field) => (e) => onChange(field, e.target.value);
    // `visible` undefined means show everything, which is what the edit form
    // does: editing is one page, never a walk through steps.
    const show = (field) => !visible || visible.includes(field);

    return (
        <div className="client-form__grid">
            {show('tier_id') && (
                <label className="client-form__field">
                    <span className="client-form__label">
                        Tier <span className="client-form__required">*</span>
                    </span>
                    <span className="client-form__control">
                        <Picker
                            value={values.tier_id}
                            onChange={(id) => onChange('tier_id', id)}
                            fetchOptions={tierOptions(projectId)}
                            resolveOption={resolveTier}
                            cacheKey={`tiers:${projectId ?? 'all'}`}
                            labelOf={tier => tier.name}
                            subtitleOf={tier => tier.tier_state}
                            selectedLabel={tierLabel}
                            searchable={false}
                            placeholder="Elegir tier..."
                            disabled={disabled || placementLocked}
                            invalid={!!errors.tier_id}
                        />
                    </span>
                    {errors.tier_id && <span className="client-form__error">{errors.tier_id}</span>}
                </label>
            )}

            {show('repository_id') && (
                <label className="client-form__field">
                    <span className="client-form__label">
                        Repositorio <span className="client-form__required">*</span>
                    </span>
                    <span className="client-form__control">
                        <Picker
                            value={values.repository_id}
                            onChange={(id) => onChange('repository_id', id)}
                            fetchOptions={repositoryOptions(projectId)}
                            resolveOption={resolveRepository}
                            cacheKey={`repositories:${projectId ?? 'all'}`}
                            labelOf={repo => repo.name || `#${repo.id}`}
                            subtitleOf={repo => repo.kind}
                            selectedLabel={repositoryLabel}
                            searchable={false}
                            placeholder="Elegir repositorio..."
                            disabled={disabled || placementLocked}
                            invalid={!!errors.repository_id}
                        />
                    </span>
                    {errors.repository_id
                        ? <span className="client-form__error">{errors.repository_id}</span>
                        : placementLocked && (
                            <span className="client-form__note">
                                Una tarea es de un solo repositorio y no se muda. Si el trabajo
                                cruza repos, son varias tareas hermanas.
                            </span>
                        )}
                </label>
            )}

            {show('title') && (
                <label className="client-form__field client-form__field--wide">
                    <span className="client-form__label">
                        Título <span className="client-form__required">*</span>
                    </span>
                    <span className="client-form__control">
                        <input
                            className={`client-form__input ${errors.title ? 'client-form__input--invalid' : ''}`}
                            type="text"
                            value={values.title}
                            onChange={set('title')}
                            maxLength={TITLE_MAX}
                            placeholder="Qué hay que hacer, en una línea"
                            disabled={disabled}
                            required
                        />
                        <span className="client-form__icon" aria-hidden="true"><IconTasks /></span>
                    </span>
                    {errors.title && <span className="client-form__error">{errors.title}</span>}
                </label>
            )}

            {show('kind') && (
                <label className="client-form__field">
                    <span className="client-form__label">Tipo</span>
                    <span className="client-form__control">
                        <select
                            className="client-form__input client-form__input--select"
                            value={values.kind}
                            onChange={set('kind')}
                            disabled={disabled}
                        >
                            {TASK_KIND_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </span>
                    {errors.kind && <span className="client-form__error">{errors.kind}</span>}
                </label>
            )}

            {show('estimate') && (
                <label className="client-form__field">
                    <span className="client-form__label">Talla</span>
                    <span className="client-form__control">
                        <select
                            className="client-form__input client-form__input--select"
                            value={values.estimate}
                            onChange={set('estimate')}
                            disabled={disabled}
                        >
                            {ESTIMATE_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </span>
                    {errors.estimate
                        ? <span className="client-form__error">{errors.estimate}</span>
                        : (
                            <span className="client-form__note">
                                No hay XL: si una tarea da XL no está desglosada, y se parte
                                antes de ponerla en la cola.
                            </span>
                        )}
                </label>
            )}

            {/* THE WEIGHT IS READ ONLY HERE, and that is the fix and not a gap.
                It moves through `PATCH /admin/tasks/{id}/weight`, its own route
                with its own event, so that a number nobody can find inside a
                content PATCH does not become the record of a priority decision
                — and so that bumping an authorized task's weight never drags
                its frozen specification along. The control arrives with T07.07. */}
            {show('priority') && (
                <label className="client-form__field">
                    <span className="client-form__label">Peso de prioridad</span>
                    <span className="client-form__control">
                        <input
                            className={`client-form__input ${errors.priority ? 'client-form__input--invalid' : ''}`}
                            type="number"
                            min={0}
                            value={values.priority}
                            onChange={set('priority')}
                            disabled={disabled || weightLocked}
                            readOnly={weightLocked}
                        />
                    </span>
                    {errors.priority
                        ? <span className="client-form__error">{errors.priority}</span>
                        : (
                            <span className="client-form__note">
                                Mayor peso primero <strong>dentro de su grupo</strong>. El trabajo
                                asignado tiene precedencia sobre la cola general.
                                {weightLocked && ' Se cambia desde su propia acción, no desde este formulario.'}
                            </span>
                        )}
                </label>
            )}

            {show('description') && (
                <label className="client-form__field client-form__field--wide">
                    <span className="client-form__label">Descripción</span>
                    <span className="client-form__control">
                        <textarea
                            className={`client-form__input client-form__input--area ${errors.description ? 'client-form__input--invalid' : ''}`}
                            value={values.description}
                            onChange={set('description')}
                            rows={4}
                            placeholder="El contexto que necesita quien la tome"
                            disabled={disabled}
                        />
                        <span className="client-form__icon client-form__icon--top" aria-hidden="true">
                            <IconNote />
                        </span>
                    </span>
                    {errors.description && <span className="client-form__error">{errors.description}</span>}
                </label>
            )}

            {/* Both are arrays in the contract. One per line here, because that
                is how a person writes a list — the translation happens in
                utils/form.js and not in anybody's head. */}
            {show('acceptance_criteria') && (
                <label className="client-form__field client-form__field--wide">
                    <span className="client-form__label">Criterios de aceptación</span>
                    <span className="client-form__control">
                        <textarea
                            className={`client-form__input client-form__input--area ${errors.acceptance_criteria ? 'client-form__input--invalid' : ''}`}
                            value={values.acceptance_criteria}
                            onChange={set('acceptance_criteria')}
                            rows={4}
                            placeholder={'Uno por línea.\nLa prueba pasa en verde.\nEl contrato queda publicado.'}
                            disabled={disabled}
                        />
                    </span>
                    {errors.acceptance_criteria
                        ? <span className="client-form__error">{errors.acceptance_criteria}</span>
                        : <span className="client-form__note">Uno por línea. Son la compuerta de la entrega.</span>}
                </label>
            )}

            {show('scope') && (
                <label className="client-form__field client-form__field--wide">
                    <span className="client-form__label">Ámbito</span>
                    <span className="client-form__control">
                        <textarea
                            className={`client-form__input client-form__input--area client-form__input--mono ${errors.scope ? 'client-form__input--invalid' : ''}`}
                            value={values.scope}
                            onChange={set('scope')}
                            rows={3}
                            placeholder={'src/features/clients/\ndocs/api-reference.md'}
                            disabled={disabled}
                        />
                    </span>
                    {errors.scope
                        ? <span className="client-form__error">{errors.scope}</span>
                        : <span className="client-form__note">Rutas que la tarea puede tocar, una por línea.</span>}
                </label>
            )}

            {/* A proposal is allowed to be incomplete and carry its doubts out
                loud — proposing demands nothing. AUTHORIZING does: a task with
                open questions cannot be authorized, so emptying this box is the
                last step before somebody signs it. */}
            {show('open_questions') && (
                <label className="client-form__field client-form__field--wide">
                    <span className="client-form__label">Dudas abiertas</span>
                    <span className="client-form__control">
                        <textarea
                            className={`client-form__input client-form__input--area ${errors.open_questions ? 'client-form__input--invalid' : ''}`}
                            value={values.open_questions}
                            onChange={set('open_questions')}
                            rows={3}
                            placeholder={'Una por línea.\n¿El listado pagina o trae todo?'}
                            disabled={disabled}
                        />
                    </span>
                    {errors.open_questions
                        ? <span className="client-form__error">{errors.open_questions}</span>
                        : (
                            <span className="client-form__note">
                                Una por línea. Una propuesta con dudas <strong>no se puede
                                autorizar</strong>: se responden antes.
                            </span>
                        )}
                </label>
            )}

            {show('status') && (
                <label className="client-form__field">
                    <span className="client-form__label">Estado del registro</span>
                    <span className="client-form__control">
                        <select
                            className="client-form__input client-form__input--select"
                            value={values.status}
                            onChange={set('status')}
                            disabled={disabled}
                        >
                            {STATUS_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </span>
                    {errors.status && <span className="client-form__error">{errors.status}</span>}
                </label>
            )}
        </div>
    );
}
